import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Weekly, Settings } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/bill-utils";
import { Input } from "@/components/ui/input";
import {
  ChevronLeft,
  ChevronRight,
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle2,
  Target,
  TrendingUp,
  Pencil,
  CalendarDays,
  AlertTriangle,
  Sparkles,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar as RBar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
} from "recharts";

const startOfWeek = (d) => {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // Monday = 0
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDays = (d, n) => {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
};

const StatCard = ({ label, value, accent = "black", icon: Icon, testid, hint }) => (
  <div data-testid={testid} className="swiss-card">
    <div className="flex items-start justify-between">
      <div className="swiss-label">{label}</div>
      {Icon && <Icon className="h-4 w-4 text-neutral-400" strokeWidth={2} />}
    </div>
    <div
      className="mt-3 font-display font-black text-xl sm:text-2xl md:text-[1.75rem] tabular-nums whitespace-nowrap overflow-hidden text-ellipsis leading-tight"
      style={{ color: accent }}
    >
      {value}
    </div>
    {hint && (
      <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-neutral-500">
        {hint}
      </div>
    )}
  </div>
);

const WeeklyPage = () => {
  const [weekStart, setWeekStart] = useState(() => toIso(startOfWeek(new Date())));
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState("1000");

  const load = async (ws) => {
    try {
      setLoading(true);
      const [s, t] = await Promise.all([
        Weekly.summary(ws),
        Weekly.trend(8),
      ]);
      setSummary(s);
      setTrend(t);
      setGoalDraft(String(s.profit_goal));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(weekStart);
  }, [weekStart]);

  const shiftWeek = (days) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + days);
    setWeekStart(toIso(startOfWeek(d)));
  };

  const thisWeek = () => setWeekStart(toIso(startOfWeek(new Date())));

  const saveGoal = async () => {
    const n = Number(goalDraft);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a valid profit goal");
      return;
    }
    try {
      await Settings.setProfitGoal(n);
      toast.success("Profit goal updated");
      setEditingGoal(false);
      load(weekStart);
    } catch {
      toast.error("Could not update profit goal");
    }
  };

  if (loading && !summary) {
    return (
      <div data-testid="weekly-loading" className="text-sm text-neutral-500">
        Loading weekly summary…
      </div>
    );
  }
  if (!summary) return null;

  const pct = (n, max) => {
    if (max <= 0) return 0;
    return Math.max(0, Math.min(100, (n / max) * 100));
  };
  // Scale: target_in (outgoing + profit goal), so both received and expected show relative to it.
  const max = Math.max(summary.target_in, summary.incoming_total, 1);
  const receivedPct = pct(summary.incoming_received_total, max);
  const expectedPct = pct(summary.incoming_total, max);
  const outgoingPct = pct(summary.outgoing_total, max);

  const weekLabel = `${fmtDate(summary.week_start)} — ${fmtDate(summary.week_end)}`;
  const now = toIso(startOfWeek(new Date()));
  const isCurrent = summary.week_start === now;

  return (
    <div data-testid="weekly-page" className="fade-rise">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="swiss-label mb-3">Weekly</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
            This week's cashflow
          </h1>
          <p className="text-neutral-600 mt-2">{weekLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="week-prev"
            onClick={() => shiftWeek(-7)}
            className="h-10 w-10 border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black flex items-center justify-center"
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="week-today"
            onClick={thisWeek}
            disabled={isCurrent}
            className={`border-2 px-4 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
              isCurrent
                ? "border-neutral-200 text-neutral-400 cursor-default"
                : "border-neutral-300 hover:border-black text-neutral-700 hover:text-black"
            }`}
          >
            This week
          </button>
          <button
            data-testid="week-next"
            onClick={() => shiftWeek(7)}
            className="h-10 w-10 border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black flex items-center justify-center"
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <Link
            data-testid="print-weekly-btn"
            to="/weekly/print"
            className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2 ml-1"
            title="Print this week's ins, outs, chart and tips"
          >
            <Printer className="h-4 w-4" /> Weekly
          </Link>
          <Link
            data-testid="print-monthly-btn"
            to="/statement?preset=this-month"
            className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
            title="Print this month's statement"
          >
            <Printer className="h-4 w-4" /> Monthly
          </Link>
          <Link
            data-testid="print-forecast-btn"
            to="/weekly/forecast"
            className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
            title="Print 3-month forecast (great for bank/loan applications)"
          >
            <Printer className="h-4 w-4" /> 3-Mo Forecast
          </Link>
          <Link
            data-testid="print-custom-btn"
            to="/statement?preset=custom"
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
            title="Print a statement for any custom date range"
          >
            <Printer className="h-4 w-4" /> Custom
          </Link>
        </div>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
        <StatCard
          testid="stat-going-out"
          label="Going Out (Bills)"
          value={fmtMoney(summary.outgoing_total)}
          accent="#FF3B30"
          icon={ArrowUpCircle}
          hint={`${summary.bills.length} bill${summary.bills.length === 1 ? "" : "s"} due`}
        />
        <StatCard
          testid="stat-overdue"
          label="Overdue"
          value={fmtMoney(summary.overdue_total)}
          accent={summary.overdue_total > 0 ? "#FF3B30" : "#008A00"}
          icon={AlertTriangle}
          hint={
            summary.overdue_total > 0
              ? `${summary.overdue_count} bill${summary.overdue_count === 1 ? "" : "s"} to clear`
              : "All current"
          }
        />
        <StatCard
          testid="stat-coming-in"
          label="Coming In (Expected)"
          value={fmtMoney(summary.incoming_expected_total)}
          accent="#B45309"
          icon={ArrowDownCircle}
          hint="Pending receivables"
        />
        <StatCard
          testid="stat-received"
          label="Received"
          value={fmtMoney(summary.incoming_received_total)}
          accent="#008A00"
          icon={CheckCircle2}
          hint="Money in the door"
        />
        <StatCard
          testid="stat-still-needed"
          label="Still Needed"
          value={fmtMoney(summary.still_needed)}
          accent={summary.still_needed > 0 ? "#000000" : "#008A00"}
          icon={Target}
          hint={
            summary.still_needed > 0
              ? "To hit profit goal"
              : "Goal already covered"
          }
        />
      </section>

      {/* Visualisation */}
      <section className="swiss-card mt-6" data-testid="weekly-viz">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="swiss-label">Progress towards target</div>
            <div className="font-display font-extrabold text-2xl mt-1 tabular-nums">
              {fmtMoney(summary.incoming_received_total)} / {fmtMoney(summary.target_in)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              Target = Bills {fmtMoney(summary.outgoing_total)} + Profit{" "}
              {fmtMoney(summary.profit_goal)}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {!editingGoal ? (
              <div className="flex items-center gap-2 border border-neutral-300 px-3 py-2">
                <Target className="h-3.5 w-3.5 text-neutral-500" />
                <span className="swiss-label">Profit goal</span>
                <span className="font-bold mono tabular-nums">
                  {fmtMoney(summary.profit_goal)}
                </span>
                <button
                  data-testid="edit-profit-goal"
                  onClick={() => {
                    setGoalDraft(String(summary.profit_goal));
                    setEditingGoal(true);
                  }}
                  className="text-neutral-500 hover:text-black ml-1"
                  aria-label="Edit profit goal"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  data-testid="profit-goal-input"
                  className="swiss-input mono w-36"
                  value={goalDraft}
                  onChange={(e) => setGoalDraft(e.target.value)}
                  inputMode="decimal"
                />
                <button
                  data-testid="save-profit-goal"
                  onClick={saveGoal}
                  className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-white hover:text-black transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingGoal(false)}
                  className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 hover:text-black"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <Bar label="Going Out" value={summary.outgoing_total} pct={outgoingPct} color="#FF3B30" />
          <Bar label="Received" value={summary.incoming_received_total} pct={receivedPct} color="#008A00" />
          <Bar label="Expected (Received + Pending)" value={summary.incoming_total} pct={expectedPct} color="#002FA7" />
          <Bar label="Target" value={summary.target_in} pct={100} color="#111827" striped />
        </div>

        <div
          className={`mt-5 border-2 p-4 ${
            summary.on_track
              ? "border-green-600 bg-green-50"
              : summary.projected_shortfall > 0
                ? "border-red-500 bg-red-50"
                : "border-amber-400 bg-amber-50"
          }`}
        >
          <div className="flex items-start gap-3">
            <TrendingUp
              className={`h-5 w-5 mt-0.5 ${summary.on_track ? "text-green-700" : "text-red-700"}`}
            />
            <div>
              <div className="font-display font-extrabold text-lg">
                {summary.on_track
                  ? "On track to hit the profit goal."
                  : summary.projected_shortfall > 0
                    ? `Short by ${fmtMoney(summary.projected_shortfall)} even if every expected receivable lands.`
                    : `Will hit goal if all expected receivables land.`}
              </div>
              <div className="text-sm text-neutral-700 mt-1">
                Still need to receive{" "}
                <span className="font-bold mono">
                  {fmtMoney(summary.still_needed)}
                </span>{" "}
                this week to clear bills and pocket{" "}
                <span className="font-bold mono">
                  {fmtMoney(summary.profit_goal)}
                </span>{" "}
                profit.
              </div>
            </div>
          </div>
        </div>

        {/* Overdue call-out — visible even when weekly target is met */}
        {summary.overdue_total > 0 && (
          <div
            data-testid="overdue-callout"
            className="mt-4 border-2 border-red-500 bg-white p-4"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 mt-0.5 text-red-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-display font-extrabold text-lg">
                  {fmtMoney(summary.overdue_total)} overdue across{" "}
                  {summary.overdue_count} bill
                  {summary.overdue_count === 1 ? "" : "s"}
                </div>
                <div className="text-sm text-neutral-700 mt-1">
                  To bring{" "}
                  <span className="font-bold">everything current</span> —
                  including this week — you need to clear{" "}
                  <span className="font-bold mono">
                    {fmtMoney(summary.target_with_catchup)}
                  </span>{" "}
                  in total
                  {summary.all_current ? "" : ` (${fmtMoney(summary.still_needed_with_catchup)} still to receive)`}
                  .
                </div>
              </div>
            </div>
          </div>
        )}
        {summary.overdue_total === 0 && (
          <div
            data-testid="overdue-clear"
            className="mt-4 border-2 border-green-600 bg-white p-4"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 mt-0.5 text-green-700 flex-shrink-0" />
              <div>
                <div className="font-display font-extrabold text-base">
                  No overdue bills — everything's current.
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Missing income suggestion */}
      {summary.projected_shortfall > 0 && (
        <section
          data-testid="income-suggestion"
          className="mt-6 swiss-card border-l-4 border-l-amber-500"
        >
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-extrabold text-lg">
                Book{" "}
                <span className="mono">
                  {fmtMoney(summary.projected_shortfall)}
                </span>{" "}
                more income to stay on target
              </div>
              <div className="text-sm text-neutral-700 mt-1">
                Currently{" "}
                <span className="font-bold mono">
                  {fmtMoney(summary.incoming_total)}
                </span>{" "}
                is booked against a target of{" "}
                <span className="font-bold mono">
                  {fmtMoney(summary.target_in)}
                </span>
                . Log outstanding install deposits, service calls or repairs
                you're expecting this week.
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to="/income"
                  data-testid="suggestion-add-income"
                  className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2.5 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
                >
                  <TrendingUp className="h-3.5 w-3.5" /> Add Income
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Multi-week trend chart */}
      {trend && trend.weeks && trend.weeks.length > 0 && (
        <section className="swiss-card mt-6" data-testid="weekly-trend">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="swiss-label">Trend — Last {trend.weeks.length} weeks</div>
              <div className="font-display font-extrabold text-xl mt-1">
                Cashflow at a glance
              </div>
            </div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500 flex-wrap">
              <LegendDot color="#FF3B30" /> Out
              <LegendDot color="#008A00" /> Received
              <span className="opacity-50">|</span>
              <span className="text-purple-700 font-bold">Expected breakdown ↓</span>
              <LegendDot color="#312E81" /> Install 50%
              <LegendDot color="#4338CA" /> Install 30%
              <LegendDot color="#6366F1" /> Install 20%
              <LegendDot color="#0891B2" /> Service
              <LegendDot color="#0EA5E9" /> Repair
              <LegendDot color="#94A3B8" /> Other
            </div>
          </div>

          {/* Clarification banner — "Expected = our scheduled deposits, not overdue" */}
          <div
            data-testid="expected-explainer"
            className="mb-4 border-l-4 border-l-indigo-500 bg-indigo-50 p-3 text-xs text-neutral-700 leading-snug"
          >
            <span className="font-bold text-indigo-900">Expected (blue/teal stack)</span> ={" "}
            your scheduled income from booked jobs — install deposits (50%
            sign-up → 30% pre-install → 20% on completion), service callouts
            and factory repairs. <span className="font-bold">These are NOT overdue</span> — they're
            simply your payment process timing. Once received, they shift into{" "}
            <span className="font-bold text-green-700">green (Received)</span>.
          </div>

          <div style={{ width: "100%", height: 280, minHeight: 280 }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={trend.weeks}
                margin={{ top: 10, right: 8, left: -10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke="#E5E7EB"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{
                    fontSize: 11,
                    fontFamily: "IBM Plex Sans",
                    letterSpacing: "0.1em",
                  }}
                  stroke="#9CA3AF"
                />
                <YAxis
                  tick={{
                    fontSize: 10,
                    fontFamily: "IBM Plex Mono",
                  }}
                  stroke="#9CA3AF"
                  tickFormatter={(v) =>
                    v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                  }
                />
                <Tooltip
                  content={<ExpectedTrendTooltip />}
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                />
                <RBar dataKey="outgoing" fill="#FF3B30" name="Out" />
                <RBar dataKey="received" fill="#008A00" name="Received" />
                {/* Stacked Expected — each segment a different shade so they read as "payment process timing" */}
                <RBar
                  dataKey="expected_install_50"
                  stackId="expected"
                  fill="#312E81"
                  name="Install deposit 50%"
                />
                <RBar
                  dataKey="expected_install_30"
                  stackId="expected"
                  fill="#4338CA"
                  name="Install deposit 30%"
                />
                <RBar
                  dataKey="expected_install_20"
                  stackId="expected"
                  fill="#6366F1"
                  name="Install deposit 20%"
                />
                <RBar
                  dataKey="expected_service"
                  stackId="expected"
                  fill="#0891B2"
                  name="Service call"
                />
                <RBar
                  dataKey="expected_repair"
                  stackId="expected"
                  fill="#0EA5E9"
                  name="Factory repair"
                />
                <RBar
                  dataKey="expected_other"
                  stackId="expected"
                  fill="#94A3B8"
                  name="Other"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* This week lists */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-6">
        <div className="swiss-card">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="h-4 w-4" />
            <h2 className="font-display font-extrabold uppercase tracking-widest text-lg">
              Bills this week
            </h2>
          </div>
          {summary.bills.length === 0 ? (
            <div className="text-sm text-neutral-500">No bills due.</div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {summary.bills.map((b) => (
                <li key={b.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/bills/${b.id}`}
                      className="font-display font-bold hover:underline truncate block"
                    >
                      {b.company}
                    </Link>
                    <div className="text-xs text-neutral-500 hash truncate">
                      #{b.invoice_number} · due {fmtDate(b.due_date)}
                    </div>
                  </div>
                  <div className="font-display font-extrabold mono tabular-nums whitespace-nowrap">
                    {fmtMoney(b.remaining_balance)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="swiss-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4" />
            <h2 className="font-display font-extrabold uppercase tracking-widest text-lg">
              Income this week
            </h2>
          </div>
          {summary.incomes.length === 0 ? (
            <div className="text-sm text-neutral-500">
              No income scheduled. <Link to="/income" className="underline">Add some</Link>.
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {summary.incomes.map((i) => (
                <li key={i.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">
                      {i.client_name}
                    </div>
                    <div className="text-xs text-neutral-500 truncate">
                      {i.category} · expected {fmtDate(i.expected_date)}
                    </div>
                  </div>
                  <div
                    className={`font-display font-extrabold mono tabular-nums whitespace-nowrap ${i.received ? "text-green-700" : "text-amber-700"}`}
                  >
                    {i.received ? "" : "+ "}
                    {fmtMoney(i.amount)}
                    {i.received && <span className="ml-1 text-[10px] uppercase tracking-widest text-green-700">Paid</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Overdue bills list — always visible when any exist */}
      {summary.overdue_bills && summary.overdue_bills.length > 0 && (
        <section className="swiss-card mt-6 border-l-4 border-l-red-500" data-testid="overdue-bills-list">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <h2 className="font-display font-extrabold uppercase tracking-widest text-lg">
              Overdue bills
            </h2>
            <span className="chip bg-red-50 text-red-700 border-red-200 ml-auto">
              {fmtMoney(summary.overdue_total)} total
            </span>
          </div>
          <ul className="divide-y divide-neutral-100">
            {summary.overdue_bills.map((b) => (
              <li
                key={b.id}
                className="py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <Link
                    to={`/bills/${b.id}`}
                    className="font-display font-bold hover:underline truncate block"
                  >
                    {b.company}
                  </Link>
                  <div className="text-xs text-neutral-500 truncate">
                    <span className="hash">#{b.invoice_number}</span> · due{" "}
                    {fmtDate(b.due_date)}
                  </div>
                </div>
                <div className="font-display font-extrabold mono tabular-nums whitespace-nowrap text-red-700">
                  {fmtMoney(b.remaining_balance)}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

const LegendDot = ({ color, striped }) => (
  <span
    aria-hidden
    className="inline-block h-2.5 w-2.5 mr-1 align-middle"
    style={{
      background: striped
        ? "repeating-linear-gradient(135deg, #111 0 3px, transparent 3px 6px)"
        : color,
    }}
  />
);

// Rich tooltip that breaks down Expected by category — makes it crystal clear
// the blue/teal stack is scheduled deposits, not overdue debt.
const ExpectedTrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload || {};
  const expectedTotal =
    (d.expected_install_50 || 0) +
    (d.expected_install_30 || 0) +
    (d.expected_install_20 || 0) +
    (d.expected_service || 0) +
    (d.expected_repair || 0) +
    (d.expected_other || 0);
  const rows = [
    { label: "Out (bills)", value: d.outgoing || 0, color: "#FF3B30" },
    { label: "Received", value: d.received || 0, color: "#008A00" },
  ];
  const expRows = [
    { label: "Install deposit 50%", value: d.expected_install_50, color: "#312E81" },
    { label: "Install deposit 30%", value: d.expected_install_30, color: "#4338CA" },
    { label: "Install deposit 20%", value: d.expected_install_20, color: "#6366F1" },
    { label: "Service call", value: d.expected_service, color: "#0891B2" },
    { label: "Factory repair", value: d.expected_repair, color: "#0EA5E9" },
    { label: "Other", value: d.expected_other, color: "#94A3B8" },
  ].filter((r) => (r.value || 0) > 0);

  return (
    <div
      data-testid="expected-tooltip"
      className="bg-white border-2 border-black p-3 text-xs min-w-[240px] shadow-lg"
    >
      <div className="font-display font-extrabold text-sm mb-2">
        Week of {label}
      </div>
      {rows.map((r) => (
        <div
          key={r.label}
          className="flex items-center justify-between gap-3 py-0.5"
        >
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5"
              style={{ background: r.color }}
            />
            <span className="text-neutral-700">{r.label}</span>
          </div>
          <span className="font-bold mono tabular-nums">
            {fmtMoney(r.value)}
          </span>
        </div>
      ))}
      {expectedTotal > 0 && (
        <>
          <div className="border-t border-neutral-200 mt-2 pt-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] uppercase tracking-widest font-bold text-indigo-700">
                Expected · scheduled deposits
              </span>
              <span className="font-bold mono tabular-nums">
                {fmtMoney(expectedTotal)}
              </span>
            </div>
            {expRows.map((r) => (
              <div
                key={r.label}
                className="flex items-center justify-between gap-3 py-0.5 pl-2 mt-1"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-2 w-2"
                    style={{ background: r.color }}
                  />
                  <span className="text-neutral-600">{r.label}</span>
                </div>
                <span className="mono tabular-nums">{fmtMoney(r.value)}</span>
              </div>
            ))}
            <div className="text-[10px] text-indigo-700 mt-2 italic leading-snug">
              Not overdue — these land per the install payment process.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Bar = ({ label, value, pct, color, striped }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-700">
        {label}
      </div>
      <div className="font-display font-extrabold mono tabular-nums">
        {fmtMoney(value)}
      </div>
    </div>
    <div className="h-3 bg-neutral-100 border border-neutral-200 relative">
      <div
        className={`h-full ${striped ? "bg-[linear-gradient(135deg,#111_25%,transparent_25%,transparent_50%,#111_50%,#111_75%,transparent_75%)] bg-[size:10px_10px]" : ""}`}
        style={{ width: `${pct}%`, background: striped ? undefined : color }}
      />
    </div>
  </div>
);

export default WeeklyPage;
